import { A, useSearchParams } from "@solidjs/router";
import type { OverviewResponse } from "contracts/api/dashboard/responses";
import { createEffect, createMemo, createSignal, Show } from "solid-js";
import { singleParam } from "../../ui/searchParam";
import { buildIssueBrief } from "../brief/briefIssueAdapter";
import { buildMapBrief } from "../brief/briefMapAdapter";
import { sessionBriefDraft, sessionIssueBriefIds } from "../brief/briefSession";
import { transientMapBrief } from "../brief/briefTransient";
import type { DeliveryBrief } from "../brief/briefTypes";
import { ModuleShell } from "../scan/ModuleShell";
import { createWebsiteStore } from "../website/store";
import { GovernPlan } from "./GovernPlan";
import { GovernQueue } from "./GovernQueue";
import { GovernRail } from "./GovernRail";
import {
  GovernEditorOverlay,
  GovernWorkflowSummary,
} from "./GovernWorkspaceParts";
import {
  governWorkItems,
  syncGovernWorkSources,
  updateGovernWork,
} from "./governSession";
import { canEditVerification } from "./governStatus";
import type { GovernEditor, GovernWorkItem } from "./governTypes";

export function GovernWorkspace(props: { websiteId: string }) {
  const context = createWebsiteStore(props.websiteId);

  return (
    <ModuleShell
      context={context}
      activeModule="govern"
      workspaceClass="signal-workspace signal-govern-workspace"
    >
      <Show
        when={context.data()}
        keyed
        fallback={
          <div class="loading signal-loading">
            Loading delivery governance...
          </div>
        }
      >
        {(data) => <GovernPreview data={data} websiteId={props.websiteId} />}
      </Show>
    </ModuleShell>
  );
}

function GovernPreview(props: { data: OverviewResponse; websiteId: string }) {
  const [search] = useSearchParams();
  const requestedWork = () => singleParam(search.work);
  const issueBriefs = createMemo(() =>
    sessionIssueBriefIds(props.websiteId).flatMap((issueId) => {
      const brief = buildIssueBrief(props.data, issueId);
      return brief ? [brief] : [];
    }),
  );
  const mapHandoff = transientMapBrief(props.websiteId);
  const mapBrief = mapHandoff ? buildMapBrief(props.data, mapHandoff) : null;
  const baseline = createMemo(() =>
    [...issueBriefs(), mapBrief].filter(
      (brief): brief is DeliveryBrief => brief !== null,
    ),
  );
  const briefs = createMemo(() =>
    baseline().map(
      (brief) => sessionBriefDraft(props.websiteId, brief.id) ?? brief,
    ),
  );
  const works = createMemo(() => governWorkItems(props.websiteId));
  const [selectedId, setSelectedId] = createSignal<string | null>(
    requestedWork() ?? works()[0]?.id ?? null,
  );
  const [editor, setEditor] = createSignal<GovernEditor | null>(null);
  const [editorTrigger, setEditorTrigger] = createSignal<HTMLElement | null>(
    null,
  );

  createEffect(() => syncGovernWorkSources(props.websiteId, briefs()));
  createEffect(() => {
    const current = selectedId();
    const currentWorks = works();
    if (!currentWorks.some((work) => work.id === current)) {
      setSelectedId(currentWorks[0]?.id ?? null);
    }
  });

  const selected = createMemo(
    () => works().find((work) => work.id === selectedId()) ?? null,
  );
  const currentBrief = createMemo(() => {
    const work = selected();
    return work
      ? (briefs().find((brief) => brief.id === work.sourceBriefId) ?? null)
      : null;
  });
  const updateSelected = (update: (work: GovernWorkItem) => GovernWorkItem) => {
    const id = selectedId();
    if (id) updateGovernWork(props.websiteId, id, update);
  };
  const openEditor = (next: GovernEditor, trigger: HTMLElement) => {
    if (
      next.kind === "check" &&
      !canEditVerification(selected() as GovernWorkItem, currentBrief())
    ) {
      return;
    }
    setEditorTrigger(trigger);
    setEditor(next);
  };
  const closeEditor = () => setEditor(null);

  return (
    <div class="signal-route-surface govern-preview">
      <header class="govern-page-head">
        <div>
          <div class="govern-title-line">
            <h1>Delivery governance</h1>
            <span>Preview</span>
          </div>
          <p>
            Control delivery, verify the result and schedule the next review.
          </p>
        </div>
        <small>Preview changes are not saved.</small>
      </header>

      <GovernWorkflowSummary works={works()} />

      <Show
        when={selected()}
        keyed
        fallback={
          <div class="govern-workspace-grid is-empty">
            <GovernQueue
              works={works()}
              briefs={briefs()}
              selectedId={selectedId()}
              websiteId={props.websiteId}
              onSelect={setSelectedId}
            />
            <section class="govern-empty-workspace">
              <span class="signal-mini-kicker">Brief-to-verification</span>
              <h2>
                No Briefs are ready for governance in this Preview session.
              </h2>
              <p>
                Complete a Brief, record its blocking inputs and mark it Ready
                for delivery before starting governance.
              </p>
              <A href={`/w/${props.websiteId}/brief`}>Open Brief</A>
            </section>
          </div>
        }
      >
        {(work) => (
          <div class="govern-workspace-grid">
            <GovernQueue
              works={works()}
              briefs={briefs()}
              selectedId={selectedId()}
              websiteId={props.websiteId}
              onSelect={setSelectedId}
            />
            <GovernPlan
              work={work}
              currentBrief={currentBrief()}
              onOpenEditor={openEditor}
              onUpdate={updateSelected}
            />
            <GovernRail work={work} currentBrief={currentBrief()} />
          </div>
        )}
      </Show>

      <Show when={editor() && selected()}>
        <GovernEditorOverlay
          editor={editor() as GovernEditor}
          work={selected() as GovernWorkItem}
          currentBrief={currentBrief()}
          returnFocus={editorTrigger()}
          onUpdate={updateSelected}
          onClose={closeEditor}
        />
      </Show>
    </div>
  );
}
